import {
  CreditCard, Fingerprint, Hand, LogIn, MapPin, QrCode, Radio, Smartphone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { TipoDispositivoLeitura } from '../../types/models';
import { TIPO_LEITOR, statusAccent } from '../../pages/admin/dispositivos-shared';
import { DispositivoEditSidePanel, type DispositivoEditRow } from './DispositivoEditSidePanel';

const TIPO_ICON: Record<TipoDispositivoLeitura, LucideIcon> = {
  RFID: CreditCard,
  QR_CODE: QrCode,
  BIOMETRIA: Fingerprint,
  CATRACA: LogIn,
  MANUAL: Hand,
  APP: Smartphone,
};

export type DispositivoFormState = {
  nome: string;
  codigo: string;
  tipo: TipoDispositivoLeitura;
  localInstalacao: string;
};

type Props = {
  form: DispositivoFormState;
  setForm: React.Dispatch<React.SetStateAction<DispositivoFormState>>;
  editing: DispositivoEditRow | null;
};

function HeroBlock({ form, setForm, editing }: Props) {
  const accent = editing
    ? statusAccent(editing.status, editing.ativo)
    : '#059669';

  return (
    <>
      <div
        className="ac-dispositivo-modal__stripe"
        style={{ background: accent }}
        aria-hidden
      />
      <div className="ac-dispositivo-modal__hero">
        <div
          className="ac-dispositivo-modal__hero-tipo"
          style={{ '--disp-accent': accent } as React.CSSProperties}
        >
          <div className="ac-dispositivo-modal__hero-icon" aria-hidden>
            <Radio size={22} />
          </div>
          <span className="ac-dispositivo-modal__hero-tipo-lbl">Tipo de leitor</span>
          <div className="ac-dispositivo-modal__tipo-grid" role="radiogroup" aria-label="Tipo de leitor">
            {TIPO_LEITOR.map(({ value, label, hint }) => {
              const Icon = TIPO_ICON[value];
              const active = form.tipo === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`ac-dispositivo-modal__tipo-btn${active ? ' ac-dispositivo-modal__tipo-btn--active' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, tipo: value }))}
                  title={hint}
                >
                  <Icon size={16} strokeWidth={1.75} />
                  <span className="ac-dispositivo-modal__tipo-btn-label">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="ac-dispositivo-modal__hero-fields">
          <label className="ac-dispositivo-field">
            <span className="ac-dispositivo-field__label">Nome do dispositivo *</span>
            <input
              className="ac-dispositivo-field__input ac-dispositivo-field__input--lg"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Ex.: Entrada principal"
              autoComplete="off"
              autoFocus
            />
          </label>
          <div className="ac-dispositivo-modal__hero-row">
            <label className="ac-dispositivo-field">
              <span className="ac-dispositivo-field__label">Código único *</span>
              <input
                className="ac-dispositivo-field__input ac-dispositivo-field__input--mono"
                value={form.codigo}
                onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value }))}
                placeholder="10203050"
                autoComplete="off"
                readOnly={!!editing}
                title={editing ? 'O código identifica o hardware na rede' : undefined}
              />
              {editing && (
                <span className="ac-dispositivo-field__hint">Identificador fixo na rede da escola</span>
              )}
            </label>
          </div>
        </div>
      </div>
    </>
  );
}

function InstalacaoSection({ form, setForm }: Pick<Props, 'form' | 'setForm'>) {
  return (
    <section className="ac-dispositivo-section">
      <div className="ac-dispositivo-section__head">
        <MapPin size={14} />
        <h3 className="ac-dispositivo-section__title">Instalação</h3>
      </div>
      <label className="ac-dispositivo-field">
        <span className="ac-dispositivo-field__label">Local / ponto de acesso</span>
        <input
          className="ac-dispositivo-field__input"
          value={form.localInstalacao}
          onChange={(e) => setForm((f) => ({ ...f, localInstalacao: e.target.value }))}
          placeholder="Ex.: Portaria bloco A, 1º andar"
          autoComplete="off"
        />
        <span className="ac-dispositivo-field__hint">
          Opcional — ajuda a localizar o equipamento nos relatórios operacionais.
        </span>
      </label>
    </section>
  );
}

export function DispositivosFormModal({ form, setForm, editing }: Props) {
  if (editing) {
    return (
      <div className="ac-modal-dual ac-dispositivo-modal-dual">
        <HeroBlock form={form} setForm={setForm} editing={editing} />
        <div className="ac-modal-dual__columns">
          <div className="ac-modal-dual__main">
            <InstalacaoSection form={form} setForm={setForm} />
          </div>
          <div className="ac-modal-dual__divider" aria-hidden />
          <DispositivoEditSidePanel dispositivo={editing} />
        </div>
      </div>
    );
  }

  return (
    <div className="ac-dispositivo-modal-dual">
      <HeroBlock form={form} setForm={setForm} editing={null} />
      <div className="ac-dispositivo-modal__body ac-dispositivo-modal__body--solo">
        <p className="ac-dispositivo-modal__intro">
          Cadastre o ponto de leitura com nome, código e tipo. O status operacional (online/offline) é atualizado pelo próprio equipamento.
        </p>
        <InstalacaoSection form={form} setForm={setForm} />
      </div>
    </div>
  );
}
